import { Box, Divider, Icons, NoData, Stack } from '@cast/design-system';
import { FailedToLoad } from '@cast/shared-messages-ui';
import { ComputeResourceType } from '@cast/shared-types';

import {
  ChartMetricsRow,
  ChartMetricsRowSkeleton,
} from '@cast/features-cluster-ui';
import { computeResourceUnitsMap } from '@cast/split-me/constants';
import {
  predictionStrokeDasharrayMap,
  percentileToColor,
  percentileToTitle,
  ChartWithPrediction,
  useCpuPredictionChartData,
  WorkloadContainerSelect,
  useWorkloadSelectedContainer,
} from '@cast/split-me/features/cluster/workload-optimization';

import { InventoryResourceMetricBox } from '../../../_components/ui/InventoryResourceMetricBox';
import { InventoryResourceMetricBoxPair } from '../../../_components/ui/InventoryResourceMetricBoxPair';
import { WidgetLayout } from '../../../_components/ui/WidgetLayout';
import { useInventoryWorkloadsTimeRangeSelection } from '../../../_hooks/useInventoryWorkloadsTimeRangeSelection';

const CPU_UNIT = computeResourceUnitsMap[ComputeResourceType.CPU];

// metric boxes: current/predicted pair + avg/predicted singles
const CPU_PREDICTION_METRIC_KEYS = [
  'rec',
  'recPredicted',
  'avg',
  'avgPredicted',
] as const;

export const CpuPredictionChart = () => {
  const { timeZone } = useInventoryWorkloadsTimeRangeSelection();
  const { container } = useWorkloadSelectedContainer();
  const {
    workload,
    isFetching,
    error,
    refetch,
    chartData,
    toggleMetric,
    isMetricDisabled,
  } = useCpuPredictionChartData({ container, timeZone });

  const isLoading = isFetching || !workload || !chartData;
  const hasPredictions = workload?.predictionInsights?.isEligible;
  const hasChartData =
    hasPredictions &&
    (chartData?.chartData?.some((point) =>
      chartData.chartDataKeys.some((key) => point[key] != null)
    ) ??
      false);
  const showEmptyState = !isLoading && !hasChartData;

  if (error && !isFetching) {
    return (
      <WidgetLayout
        title="Predictive autoscaling: CPU"
        testId="cpu-prediction-chart"
        headerEndContent={<WorkloadContainerSelect />}
      >
        <FailedToLoad
          title="Failed to load CPU predictive autoscaling"
          refresh={refetch}
        />
      </WidgetLayout>
    );
  }

  return (
    <WidgetLayout
      title="Predictive autoscaling: CPU"
      testId="cpu-prediction-chart"
      headerEndContent={<WorkloadContainerSelect />}
    >
      <Stack gap={12} height="100%">
        {isLoading ? (
          <ChartMetricsRowSkeleton
            alwaysRow
            gap={12}
            count={CPU_PREDICTION_METRIC_KEYS.length}
            testId="cpu-prediction-metrics-skeleton"
          />
        ) : chartData && hasChartData ? (
          <Stack
            direction="row"
            alignItems="flex-end"
            gap={24}
            sx={{ whiteSpace: 'nowrap' }}
          >
            <InventoryResourceMetricBoxPair
              currentDataKey="rec"
              currentValue={chartData.recommended}
              recommendedDataKey="recPredicted"
              recommendedValue={chartData.predictionsAggregated?.avgRec}
              unit={CPU_UNIT}
              colorMap={percentileToColor}
              titleMap={percentileToTitle}
              toggleMetric={toggleMetric}
              isMetricDisabled={isMetricDisabled}
              size="m"
              alignItems="flex-end"
              gap={12}
              arrowWeight="regular"
            />
            <Divider orientation="vertical" flexItem />
            <ChartMetricsRow alwaysRow alignItems="flex-end" gap={16}>
              <InventoryResourceMetricBox
                dataKey="avg"
                value={chartData.usageSummaryWithPredictions?.avg}
                unit={CPU_UNIT}
                testId="cpu-prediction-avg-usage"
                colorMap={percentileToColor}
                titleMap={percentileToTitle}
                toggleMetric={toggleMetric}
                isMetricDisabled={isMetricDisabled}
                size="s"
              />
              <InventoryResourceMetricBox
                dataKey="avgPredicted"
                value={chartData.usageSummaryWithPredictions?.avgPredicted}
                unit={CPU_UNIT}
                testId="cpu-prediction-predicted-usage"
                colorMap={percentileToColor}
                titleMap={percentileToTitle}
                toggleMetric={toggleMetric}
                isMetricDisabled={isMetricDisabled}
                size="s"
              />
            </ChartMetricsRow>
          </Stack>
        ) : null}
        <Box flex={1}>
          {showEmptyState ? (
            <NoData
              title="No predictive data"
              body="Predictive autoscaling data is not available for this workload or time range."
              icon={<Icons.Question weight="fill" />}
              testId="cpu-prediction-chart-empty"
            />
          ) : (
            <ChartWithPrediction
              timeZone={timeZone}
              unit="CPU"
              analyzeDescription="CPU predictive autoscaling"
              data={hasChartData ? chartData?.chartData ?? [] : []}
              dataKeys={chartData?.chartDataKeys ?? []}
              colorMap={percentileToColor}
              tooltipTitleMap={percentileToTitle}
              strokeDasharrayMap={
                chartData?.isReadOnly ? predictionStrokeDasharrayMap : {}
              }
              isLoading={isLoading}
              hasError={!!error && !isFetching}
              refetch={refetch}
              isMetricDisabled={isMetricDisabled}
            />
          )}
        </Box>
      </Stack>
    </WidgetLayout>
  );
};
